/**
 * Persistent house labels for choose-house plan.
 * Renders HTML cards positioned by plan_assign label_x/label_y (px).
 */
export function initPlanHouseLabels(): void {
  const plan = document.querySelector<HTMLElement>('.svg-plan-container.main-plan');
  if (!plan) {
    return;
  }

  const links = plan.querySelectorAll<HTMLAnchorElement>(
    '.svg-wrap a[data-type="house"][data-label-x][data-label-y]'
  );
  if (links.length < 1) {
    return;
  }

  const oldLayer = plan.querySelector<HTMLElement>('.plan-house-label-layer');
  if (oldLayer) {
    oldLayer.remove();
  }

  const layer = document.createElement('div');
  layer.className = 'plan-house-label-layer';
  plan.appendChild(layer);

  const svg = plan.querySelector<SVGSVGElement>('.svg-wrap svg');
  const base = resolveSvgBaseSize(svg, plan);

  links.forEach((link) => {
    const xRaw = (link.dataset.labelX ?? '').trim();
    const yRaw = (link.dataset.labelY ?? '').trim();
    const xPct = normalizeToPercent(xRaw, base.width);
    const yPct = normalizeToPercent(yRaw, base.height);
    if (!Number.isFinite(xPct) || !Number.isFinite(yPct)) {
      return;
    }

    const status = (link.dataset.planStatus ?? '').trim().toLowerCase();
    const isPlanuojamasBadge = status === 'planuojamas';
    const isDark =
      !isPlanuojamasBadge && (status === 'suplanuotas' || status === 'parduotas');
    const comment = (link.dataset.comment ?? '').trim();
    const commentColor = (link.dataset.commentColor ?? 'default').trim().toLowerCase();

    const title = (link.dataset.houseName ?? link.dataset.title ?? '').trim();
    if (title === '') {
      return;
    }

    const line2 = comment !== '' ? comment : statusLabel(status);
    if (line2 === '') {
      return;
    }

    const label = document.createElement('a');
    label.className = [
      'house-label',
      isPlanuojamasBadge
        ? 'house-label--planuojamas'
        : isDark
          ? 'house-label--dark'
          : 'house-label--light',
    ].join(' ');
    label.href = link.getAttribute('href') || '#';
    label.style.left = `${xPct}%`;
    label.style.top = `${yPct}%`;
    label.setAttribute('aria-label', `${title} — ${line2}`);
    if (link.getAttribute('aria-disabled') === 'true') {
      label.setAttribute('aria-disabled', 'true');
      label.classList.add('is-disabled');
    }

    const titleEl = document.createElement('span');
    titleEl.className = 'house-label__title';
    titleEl.textContent = title;
    label.appendChild(titleEl);

    const textEl = document.createElement('span');
    textEl.className = 'house-label__text';
    if (comment !== '' && (commentColor === 'green' || commentColor === 'red')) {
      textEl.classList.add(`house-label__text--${commentColor}`);
    }
    textEl.textContent = line2;
    label.appendChild(textEl);

    layer.appendChild(label);
  });
}

function resolveSvgBaseSize(
  svg: SVGSVGElement | null,
  fallbackEl: HTMLElement
): { width: number; height: number } {
  if (svg) {
    const vb = svg.viewBox?.baseVal;
    if (vb && vb.width > 0 && vb.height > 0) {
      return { width: vb.width, height: vb.height };
    }
    const w = Number(svg.getAttribute('width'));
    const h = Number(svg.getAttribute('height'));
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
      return { width: w, height: h };
    }
  }
  return {
    width: Math.max(1, fallbackEl.clientWidth),
    height: Math.max(1, fallbackEl.clientHeight),
  };
}

function normalizeToPercent(raw: string, baseSize: number): number {
  if (raw === '') {
    return Number.NaN;
  }
  if (raw.endsWith('%')) {
    return parseFloat(raw.slice(0, -1));
  }

  const value = parseFloat(raw);
  if (!Number.isFinite(value)) {
    return Number.NaN;
  }

  // Backward compatibility:
  // - values > 100 are treated as px and converted to % from SVG base size
  // - values 0..100 are treated as percentage
  if (value > 100 && baseSize > 0) {
    return (value / baseSize) * 100;
  }
  return value;
}

function statusLabel(status: string): string {
  switch (status) {
    case 'statomas':
      return 'Statomas';
    case 'pastatytas':
      return 'Vyksta pardavimai';
    case 'suplanuotas':
      return 'Planuojamas';
    case 'planuojamas':
      return 'Planuojamas';
    case 'parduotas':
      return 'Parduotas';
    case 'parkingas':
      return 'Parkingas';
    default:
      return '';
  }
}
